/**
 * recordAtoms — small pure helpers shared by the Record index rows and the
 * case-file byline. Lifted out of the YAG-243 preview so the shipped surfaces
 * and the fixture preview read the same provenance line.
 */
import type { WorkItem } from "@yagni/shared";

/**
 * The provenance kicker: who put this on the record, and how sure they were.
 * Derived entirely from fields the work item already carries — no new column.
 * Returns null for shapes with nothing worth saying (a bare user item).
 */
export function originLine(item: WorkItem): string | null {
  switch (item.originType) {
    case "agent": {
      const conf =
        typeof item.confidence === "number" ? ` · ${Math.round(item.confidence * 100)}% sure` : "";
      return `proposed by the Team${conf}`;
    }
    case "schedule":
      return "on schedule";
    case "webhook":
      return item.source ? `from a ${item.source} signal` : "from a signal";
    case "user": {
      // A ticket a person assigned to yagni in their tracker: the assignment
      // is its provenance, before and after anyone starts it.
      if (item.sourceRef?.ingestedVia === "assignment") {
        const by = `assigned by ${startedByFirstName(item.sourceRef.assignedByName)}`;
        const tracker = trackerName(item.source ?? item.sourceRef.source);
        return tracker ? `${by} in ${tracker}` : by;
      }
      // A ticket a person started on the Team (Start, Add tickets, the rail's
      // paste): credit them by first name.
      if (item.sourceRef?.acceptedVia === "person") {
        return `started by ${startedByFirstName(item.sourceRef.acceptedByName)}`;
      }
      // A ticket a person imported: theirs, but nobody has started it yet
      // (import never spends; their Accept does).
      if (typeof item.sourceRef?.acceptedByName === "string") {
        return `added by ${startedByFirstName(item.sourceRef.acceptedByName)}`;
      }
      return "you asked";
    }
    default:
      return null;
  }
}

/** The tracker's display name, or null for anything that is not one. */
function trackerName(tracker: unknown): "Jira" | "Linear" | null {
  if (tracker === "jira") return "Jira";
  if (tracker === "linear") return "Linear";
  return null;
}

/** The starter's first name, from the name the ticket was stamped with. */
function startedByFirstName(name: unknown): string {
  const first = typeof name === "string" ? name.trim().split(/\s+/)[0] : "";
  return first || "a teammate";
}
